import { supabase } from "@/integrations/supabase/client";

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

async function callGroupFunction(body: Record<string, any>) {
  // Send user's JWT if authenticated, otherwise anon key
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || ANON_KEY;
  
  const response = await fetch(
    `https://${PROJECT_ID}.supabase.co/functions/v1/group-session`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'apikey': ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    }
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || 'Request failed');
  }
  return response.json();
}

export function getGuestId(): string {
  let id = localStorage.getItem('ayeris_guest_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('ayeris_guest_id', id);
  }
  return id;
}

export async function createGroupSession(opts: {
  mediaType: string;
  maxRuntime: number;
  decisionTimer: number;
  displayName: string;
  userId?: string;
}) {
  return callGroupFunction({
    action: 'create',
    media_type: opts.mediaType,
    max_runtime: opts.maxRuntime,
    decision_timer: opts.decisionTimer,
    display_name: opts.displayName,
    user_id: opts.userId || null,
    guest_id: opts.userId ? null : getGuestId(),
  });
}

export async function joinGroupSession(roomCode: string, displayName: string, userId?: string) {
  return callGroupFunction({
    action: 'join',
    room_code: roomCode,
    display_name: displayName,
    user_id: userId || null,
    guest_id: userId ? null : getGuestId(),
  });
}

export async function advanceStatus(sessionId: string, status: string) {
  return callGroupFunction({ action: 'advance_status', session_id: sessionId, status });
}

export async function submitPreferences(memberId: string, genres: string[], decades: string[]) {
  return callGroupFunction({ action: 'submit_preferences', member_id: memberId, genres, decades });
}

export async function generateGroupQueue(sessionId: string, providerIds?: number[], region?: string) {
  return callGroupFunction({ action: 'generate_queue', session_id: sessionId, provider_ids: providerIds || [], region: region || 'GB' });
}

export async function extendGroupQueue(sessionId: string, pageStart: number, providerIds?: number[], region?: string) {
  return callGroupFunction({ action: 'extend_queue', session_id: sessionId, page_start: pageStart, provider_ids: providerIds || [], region: region || 'GB' });
}

export async function submitGroupSwipes(
  memberId: string,
  sessionId: string,
  swipes: { tmdb_id: number; action: string }[]
) {
  return callGroupFunction({ action: 'submit_swipes', member_id: memberId, session_id: sessionId, swipes });
}

export async function markSwipingDone(memberId: string) {
  return callGroupFunction({ action: 'mark_done', member_id: memberId });
}

export async function calculateGroupResults(sessionId: string) {
  return callGroupFunction({ action: 'calculate_results', session_id: sessionId });
}

export async function confirmGroupResult(memberId: string, sessionId: string) {
  return callGroupFunction({ action: 'confirm', member_id: memberId, session_id: sessionId });
}

export async function leaveGroupSession(memberId: string, sessionId: string) {
  return callGroupFunction({ action: 'leave_session', member_id: memberId, session_id: sessionId });
}

export async function endGroupSession(sessionId: string) {
  return callGroupFunction({ action: 'end_session', session_id: sessionId });
}

// Edge function reads for guests (bypasses RLS via service role)
export async function readSession(sessionId: string, guestId?: string) {
  const result = await callGroupFunction({ action: 'read_session', session_id: sessionId, guest_id: guestId });
  return result.session;
}

export async function readMembers(sessionId: string, guestId?: string) {
  const result = await callGroupFunction({ action: 'read_members', session_id: sessionId, guest_id: guestId });
  return result.members || [];
}

export async function readRoomCode(sessionId: string) {
  const result = await callGroupFunction({ action: 'read_room_code', session_id: sessionId });
  return result.room_code;
}

// Realtime subscriptions — authenticated users use direct Supabase subscriptions,
// guests use polling via edge function since RLS requires auth.uid()

export function subscribeToSession(
  sessionId: string,
  onChange: (session: any) => void,
  guestId?: string
) {
  // Guest: poll via edge function
  if (guestId) {
    let active = true;
    const poll = async () => {
      while (active) {
        try {
          const session = await readSession(sessionId, guestId);
          if (session && active) onChange(session);
        } catch { }
        await new Promise(r => setTimeout(r, 2000));
      }
    };
    poll();
    return { unsubscribe: () => { active = false; } };
  }

  // Authenticated: direct realtime subscription
  return supabase
    .channel(`group-session-${sessionId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'group_sessions',
      filter: `id=eq.${sessionId}`,
    }, (payload) => onChange(payload.new))
    .subscribe();
}

export function subscribeToMembers(
  sessionId: string,
  onChange: (members: any[]) => void,
  guestId?: string
) {
  // Guest: poll via edge function
  if (guestId) {
    let active = true;
    const poll = async () => {
      while (active) {
        try {
          const members = await readMembers(sessionId, guestId);
          if (members && active) onChange(members);
        } catch { }
        await new Promise(r => setTimeout(r, 2000));
      }
    };
    poll();
    return { unsubscribe: () => { active = false; } };
  }

  // Authenticated: direct query + realtime subscription
  const fetchMembers = async () => {
    const { data } = await supabase
      .from('group_members')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    if (data) onChange(data);
  };

  fetchMembers();

  return supabase
    .channel(`group-members-${sessionId}`)
    .on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'group_members',
      filter: `session_id=eq.${sessionId}`,
    }, () => fetchMembers())
    .subscribe();
}
